import type { CSSProperties, ChangeEvent, ClipboardEvent, KeyboardEvent, MouseEvent } from 'react';
import { forwardRef } from 'react';

import type {
  A11y,
  BasicValue,
  Classnames,
  IconList,
  InputSize,
  InputType,
} from '@snowit/icetypes';
import type {
  ButtonClick,
  InputOnChange,
  InputOnClick,
  InputOnFocus,
  InputOnKeyDown,
} from '@snowit/icetypes/events';

import useA11y from '@snowit/hooks/useA11y';
import testId from '@snowit/utils/testId';

import BadgeLabel from '../../Badges/BadgeLabel';
import ButtonIcon from '../../Buttons/ButtonIcon';
import Icon from '../../Icon';
import Loader from '../../Loaders/LoaderSpinner';
import Typo from '../../Typography/Typo';

import cx from 'classnames';
import s from './Input.module.scss';

export interface IInput {
  type?: InputType;
  size?: InputSize;
  label?: string;
  hint?: string;
  placeholder?: string;
  icon?: IconList;
  iconColor?: string;
  iconButton?: IconList;
  name?: string;
  defaultValue?: string;
  value?: BasicValue;
  valueSecondary?: string;
  valueSecondaryTheme?: 'default' | 'badge';
  maxLength?: number;
  min?: number;
  max?: number;
  prefix?: string;
  error?: boolean;
  errorMsg?: string;
  loading?: boolean;
  fakeInput?: boolean;
  readOnly?: boolean;
  required?: boolean;
  disabled?: boolean;
  dropdown?: boolean;
  autoComplete?: string;
  onChange?: (params: InputOnChange) => void;
  onClick?: (params: InputOnClick) => void;
  onClickButton?: (params: ButtonClick) => void;
  onBlur?: (params: InputOnFocus) => void;
  onFocus?: (params: InputOnFocus) => void;
  onKeyDown?: (params: InputOnKeyDown) => void;
  onKeyUp?: (params: InputOnKeyDown) => void;
  onEnter?: (params: InputOnKeyDown) => void;
  onEsc?: (params: InputOnKeyDown) => void;
  onPaste?: (event: ClipboardEvent<HTMLInputElement>) => void;
  id?: string;
  classNames?: Classnames<'wrapper' | 'wrapInput' | 'input' | 'label'>;
  a11y?: A11y;
  /** Jest/Cypress test string */
  test?: string;
  step?: number;
}

const Input = forwardRef<HTMLInputElement, IInput>((props, ref) => {
  const {
    classNames,
    defaultValue,
    disabled,
    dropdown,
    error,
    errorMsg,
    fakeInput,
    hint,
    icon,
    iconColor,
    iconButton,
    label,
    loading,
    name,
    maxLength,
    onBlur,
    onChange,
    onClick,
    onClickButton,
    onFocus,
    onKeyDown,
    onKeyUp,
    onEnter,
    onEsc,
    onPaste,
    placeholder,
    readOnly,
    required,
    size = 'tr',
    test,
    type = 'text',
    value,
    valueSecondary,
    valueSecondaryTheme = 'default',
    autoComplete,
    a11y,
    ...rest
  } = props;

  const a11yH = useA11y();

  const id = a11yH.makeId(props.id);
  const hintId = a11yH.makeId(props.id, 'hint');
  const errorId = a11yH.makeId(props.id, 'error');

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (disabled || loading) return;
    if (typeof onClick === 'function') {
      onClick({
        name,
        id,
        value: String(value ?? ''),
        event,
      });
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (disabled || loading) return;
    if (typeof onChange === 'function') {
      onChange({
        name,
        id,
        value: String(event.target.value ?? ''),
        event,
      });
    }
  };

  const handleFocus = (event) => {
    if (disabled || loading) return;
    if (typeof onFocus === 'function') {
      onFocus({
        name,
        id,
        event,
      });
    }
  };

  const handleBlur = (event) => {
    if (disabled || loading) return;
    if (typeof onBlur === 'function') {
      onBlur({
        name,
        id,
        event,
      });
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (disabled || loading) return;
    const fullEvent: InputOnKeyDown = {
      name,
      id,
      value: String(event.currentTarget.value ?? ''),
      event,
      key: event.key,
      modifiers: {
        alt: event.altKey,
        ctrl: event.ctrlKey,
        shift: event.shiftKey,
        meta: event.metaKey,
      },
      keyCode: event.keyCode,
    };
    if (typeof onEnter === 'function' && event.key === 'Enter') {
      onEnter(fullEvent);
    }
    if (typeof onEsc === 'function' && event.key === 'Esc') {
      onEsc(fullEvent);
    }
    if (typeof onKeyDown === 'function') {
      onKeyDown(fullEvent);
    }
  };

  const handleKeyUp = (event) => {
    if (disabled || loading) return;
    if (typeof onKeyUp === 'function') {
      onKeyUp({
        name,
        id,
        value: event.currentTarget.value,
        event,
        key: event.key,
        modifiers: {
          alt: event.altKey,
          ctrl: event.ctrlKey,
          shift: event.shiftKey,
          meta: event.metaKey,
        },
        keyCode: event.keyCode,
      });
    }
  };

  const renderInput = () => {
    let ariaDescribedby: string | undefined;
    if (errorMsg) {
      ariaDescribedby = errorId;
    } else if (hint) {
      ariaDescribedby = hintId;
    }
    return (
      <input
        className={cx(s.input, classNames?.input)}
        disabled={disabled || loading}
        id={id}
        name={name}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onPaste={onPaste}
        placeholder={placeholder}
        readOnly={readOnly || fakeInput}
        ref={ref}
        required={required}
        type={type}
        pattern={type === 'number' ? '\\d*' : undefined}
        value={value ?? ''}
        defaultValue={defaultValue}
        autoComplete={autoComplete || id}
        maxLength={maxLength}
        role={a11y?.role}
        aria-expanded={a11y?.expanded}
        aria-controls={a11y?.controls}
        aria-describedby={ariaDescribedby}
        aria-invalid={error}
        aria-label={label ? undefined : label}
        {...testId(test)}
        {...rest}
      />
    );
  };

  const renderLoader = () => {
    if (!loading) return null;
    return <Loader className={cx(s.iconAfter, s.loader)} size={16} />;
  };

  const renderIcon = () => {
    if (!icon) return null;
    return <Icon className={cx(s.iconBefore, s.icon)} name={icon} a11y={{ hidden: true }} />;
  };

  const renderArrow = () => {
    if (loading) return null;
    if (!dropdown) return null;
    if (onClickButton) return null;
    return (
      <Icon
        className={cx(s.iconAfter, s.arrow)}
        name={'arrow_angle_down'}
        a11y={{ hidden: true }}
      />
    );
  };

  const renderButton = () => {
    if (dropdown) return null;
    if (!onClickButton) return null;

    return (
      <ButtonIcon
        classNames={{ button: s.btn }}
        icon={iconButton}
        onClick={onClickButton}
        theme={'transparent'}
        size={size === 'db' ? 'xxl' : 'db'}
        color={'text'}
        type={'button'}
        rounded
      />
    );
  };

  const renderLabel = () => {
    if (!label) return null;
    if (size !== 'tr') return null;
    return (
      <Typo
        className={cx(s.label, classNames?.label)}
        text={label}
        tag={'label'}
        size={'df'}
        a11y={{ htmlFor: id }}
      />
    );
  };

  const renderValueSecondary = () => {
    if (!valueSecondary) return null;
    if (valueSecondaryTheme === 'badge') {
      return <BadgeLabel className={s.valSecondary} label={valueSecondary} size={'sm'} />;
    }
    return (
      <Typo className={s.valSecondary} text={valueSecondary} tag={'p'} size={'df'} color={'text'} />
    );
  };

  const renderHint = () => {
    if (error && errorMsg) return null;
    if (!hint) return null;
    return (
      <Typo className={s.hint} id={hintId} text={hint} tag={'p'} size={'md'} color={'text-l'} />
    );
  };

  const renderError = () => {
    if (!error) return null;
    if (!errorMsg) return null;
    if (error && !errorMsg) return null;
    return (
      <Typo
        className={s.hint}
        id={errorId}
        text={errorMsg}
        tag={'p'}
        size={'md'}
        color={'error'}
        a11y={{
          role: 'alert',
        }}
        {...testId(test, 'input_error')}
      />
    );
  };

  return (
    <div className={cx(s.wrapper, classNames?.wrapper)} {...testId(test, 'wrapper')}>
      <div
        className={cx(s.wrapInput, classNames?.wrapInput, {
          [s.hasLabel]: label && size === 'tr',
          [s.hasIconBefore]: icon,
          [s.hasIconAfter]: dropdown || onClickButton || loading,
          [s.dropdown]: dropdown,
          [s.fakeInput]: fakeInput,
          [s.hasValSecondary]: valueSecondary,
          [s.required]: required,
          [s.readOnly]: readOnly || loading,
          [s.error]: error,
          [s.disabled]: disabled,
        })}
        style={
          {
            '--lc-icon': iconColor,
            '--l-h': `var(--s-${size})`,
          } as CSSProperties
        }
        {...testId(test, 'container')}
        onClick={handleClick}
        aria-expanded={a11y?.expanded}
        aria-controls={a11y?.controls}
      >
        {renderInput()}
        {renderLabel()}
        {renderIcon()}
        {renderLoader()}
        {renderArrow()}
        {renderValueSecondary()}
        {renderButton()}
      </div>
      {renderHint()}
      {renderError()}
    </div>
  );
});
export default Input;
